import * as React from 'react';
import {connect} from 'react-redux';
import {
    UserSession, DisplayFeatureType,
    DisplayFeature, AssociationScope
} from '../../contract-features-3/domain';
import {
    toggleAssociation,
    autoAssociate,
    disAssociate,
    clearAutoAssociationFeedback,
    setNvdbSearchDistance,
    AutoAssociate,
    ClearAutoAssociationFeedback,
    SetNvdbSearchDistance,
    DisAssociate, ToggleAssociation, ReAssociate, reAssociate,
} from "../../../actions/actions";
import * as Actions from "../../../actions/actions";
import FeatureTypeComponent from "./FeatureTypeComponent";
import {AssociatedFeature} from "../domain/AssociatedFeature";
import AutoAssociateButton from "./AutoAssociateButton";
import {AssociationJson} from "../domain/AssociationJson";
import {FeatureTypeSummary} from "../../../../integration/representation/feature-type";
import AssociationsState, {FeatureTypeAttributes} from "../state/AssociationsState";
import {Definition} from "../domain/Definition";

interface StateProps {
    user: UserSession;
    nvdbSearchDistance: number;
    selectedFeatureType: DisplayFeatureType;
    featureTypes: FeatureTypeSummary[];
    associations: Array<AssociatedFeature>;
    associationsFlat: Array<AssociationJson>
    selectedSourceFeature: DisplayFeature;
    selectedAssociation: AssociatedFeature;
    attributePopoverData: Definition[];
    autoAssociateCount: number;
    tab: AssociationScope;
    targetType: FeatureTypeAttributes;
    associationTypeId: number;
    nvdbFeatureTypes: FeatureTypeSummary[];
    isFetching: boolean;
}

interface DispatchProps {
    unselectFeatureType: Function;
    setNvdbSearchDistance: SetNvdbSearchDistance;
    toggleAssociation: ToggleAssociation;
    onAutoAssociate: AutoAssociate;
    disAssociateHandler: DisAssociate;
    reAssociateHandler: ReAssociate;
    clearAutoAssociationFeedback: ClearAutoAssociationFeedback;
}

interface AssociationTreeProps extends StateProps, DispatchProps {
    loading: boolean;
}

interface AssociationTreeState {
    searchFilter: string,
    exportingFilter: string,
    associatedStateFilter: string
}

type CompareType = (a:AssociatedFeature, b:AssociatedFeature) => number;

const compareFeatureNames = (intlCollator:Intl.Collator, disableDescriptiveName:boolean):CompareType => {
    return function compareWithIntlCollator (a:AssociatedFeature, b:AssociatedFeature):number {
        if (disableDescriptiveName) {
            return intlCollator.compare(a.name, b.name);
        } else {
            return intlCollator.compare(a.descriptiveName, b.descriptiveName);
        }
    }
}

const sortFeatures = (feature: AssociatedFeature[], intlCollator:Intl.Collator, disableDescriptiveName:boolean):AssociatedFeature[] => {
    feature.sort(compareFeatureNames(intlCollator, disableDescriptiveName));
    return feature;
}

let intlCollator = Intl.Collator('nb', { numeric: true });

class AssociationTree extends React.Component<AssociationTreeProps, AssociationTreeState> {

    constructor(props: AssociationTreeProps) {
        super(props);

        this.state = {
            searchFilter: '',
            exportingFilter: "NONE",
            associatedStateFilter: "all"
        }
    }

    associationClickHandler(association: AssociatedFeature) {
        this.props.toggleAssociation(association)
    }

    hasChildren(association: AssociatedFeature): Boolean {
        return (association.childFeatures.length > 0);
    }

    featureToggleEnabled(toggle: string): boolean {
        const {user} = this.props;
        return user?.featureToggles?.some(enabledToggle => enabledToggle === toggle) ?? false;
    }

    render() {

        const {
            featureTypes,
            associations,
            selectedSourceFeature,
            selectedAssociation,
            selectedFeatureType,
            disAssociateHandler,
            reAssociateHandler,
            attributePopoverData,
            nvdbFeatureTypes
        } = this.props;

        // FeatureToggles
        const disableDescriptiveName = this.featureToggleEnabled("DisableDescriptiveFeatureNameInAssociationAndLocationTabs");

        // filtering associations by state
        const filterState = this.state.associatedStateFilter;

        let filteredAssociations = associations;

        if (filterState === "assigned") {
            filteredAssociations = associations.filter(assoc => this.hasChildren(assoc));
        } else if (filterState === "unassigned") {
            filteredAssociations = associations.filter(assoc => !this.hasChildren(assoc));
        }

        // Some mothers might be types not in the project but from NVDB
        let featureMap: { [key: number]: boolean } = {};
        featureTypes.forEach(f => featureMap[f.typeid] = true);
        const allFeatureTypes: FeatureTypeSummary[] = featureTypes
            .concat(nvdbFeatureTypes.filter(f => !featureMap[f.typeid]))
            .sort((a, b) => {
                return a.sortNum - b.sortNum
            });

        // joining types with their features
        const featureElements = allFeatureTypes.map(type => {

            const childAssociations =
                filteredAssociations
                    .filter(assoc => assoc.featureTypeId === type.typeid);

            return (
                <FeatureTypeComponent key={type.typeid}
                                      featureType={type}
                                      associationElements={sortFeatures(childAssociations, intlCollator, disableDescriptiveName)}
                                      selectedFeatureType={selectedFeatureType}
                                      associationClickHandler={this.associationClickHandler.bind(this)}
                                      selectedFeature={selectedSourceFeature}
                                      selectedAssociation={selectedAssociation}
                                      disAssociateHandler={disAssociateHandler}
                                      reAssociateHandler={reAssociateHandler}
                                      attributePopoverData={attributePopoverData}
                                      disableDescriptiveName={disableDescriptiveName}
                />
            )
        });

        return (
            <div id="associations-tree">
                {this.props.loading ? (
                    <div className="cssloader" title="Vent litt..."/>
                ) : (
                    <>
                        <div>
                            <div id="associationsTools">
                                <AutoAssociateButton
                                    autoAssociateCount={this.props.autoAssociateCount}
                                    clearAutoAssociationFeedback={this.props.clearAutoAssociationFeedback}
                                    onAutoAssociate={this.props.onAutoAssociate}
                                    featureTypes={this.props.featureTypes}
                                    tab={this.props.tab}
                                    associationTypeId={this.props.associationTypeId}
                                    targetType={this.props.targetType}
                                    nvdbSearchDistance={this.props.nvdbSearchDistance}
                                    isFetching={this.props.isFetching}
                                />
                            </div>
                        </div>
                        <div id="featurelist">{featureElements}</div>
                    </>
                )}
            </div>
        )
    }
}


const mapFeatureListStateToProps = (state: AssociationsState): StateProps => {
    let selectedFeatureType = state.selectedFeatureType;
    return {
        user: state.user,
        featureTypes: state.featureTypes,
        associations: state.associations,
        associationsFlat: state.associationsFlat,
        selectedFeatureType: selectedFeatureType ? selectedFeatureType : null,
        selectedSourceFeature: state.selectedSourceFeature,
        selectedAssociation: state.selectedAssociation,
        attributePopoverData: state.attributePopoverData,
        autoAssociateCount: state.autoAssociateCount,
        tab: state.tab,
        targetType: state.selectedAssociationTargetType,
        associationTypeId: state.selectedAssociation ? state.selectedAssociation.featureTypeId : null,
        nvdbFeatureTypes: state.nvdbFeaturetypes,
        nvdbSearchDistance: state.nvdbSearchDistance,
        isFetching: state.isFetching
    };
};

const mapDispatchToProps: DispatchProps = ({
    toggleAssociation: toggleAssociation,
    unselectFeatureType: Actions.unselectFeatureType,
    onAutoAssociate: autoAssociate,
    disAssociateHandler: disAssociate,
    reAssociateHandler: reAssociate,
    clearAutoAssociationFeedback: clearAutoAssociationFeedback,
    setNvdbSearchDistance: setNvdbSearchDistance
});
export default connect(mapFeatureListStateToProps, mapDispatchToProps)(AssociationTree);
